import React, { useEffect, useState } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton } from "components/misc/Buttons";
import { getBlogs, deleteBlog } from "api/blog";
import PostFormModal from "components/modals/PostFormModal";
import Cookies from 'js-cookie';
import { Helmet } from "react-helmet-async";
import { ReactComponent as EditIcon } from "feather-icons/dist/icons/edit-2.svg";
import { ReactComponent as TrashIcon } from "feather-icons/dist/icons/trash-2.svg";

const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900`;
const Posts = tw.div`mt-6 sm:-mr-8 flex flex-wrap`;
const PostContainer = styled.div`
  ${tw`mt-10 w-full sm:w-1/2 lg:w-1/3 sm:pr-8`}
  ${props =>
    props.featured &&
    css`
      ${tw`w-full!`}
      ${Post} {
        ${tw`sm:flex-row! h-full sm:pr-4`}
      }
      ${Image} {
        ${tw`sm:h-96 sm:min-h-full sm:w-1/2 lg:w-2/3 sm:rounded-t-none sm:rounded-l-lg`}
      }
      ${Info} {
        ${tw`sm:-mr-4 sm:pl-8 sm:flex-1 sm:rounded-none sm:rounded-r-lg sm:border-t-2 sm:border-l-0`}
      }
      ${Description} {
        ${tw`text-sm mt-3 leading-loose text-gray-600 font-medium`}
      }
    `}
`;
const Post = styled.div`
  position: relative;
  ${tw`cursor-pointer flex flex-col bg-gray-100 rounded-lg`}
`;
const Image = styled.div`
  ${props => css`background-image: url("${props.imageSrc}");`}
  ${tw`h-64 w-full bg-cover bg-center rounded-t-lg`}
`;
const Info = tw.div`p-8 border-2 border-t-0 rounded-lg rounded-t-none`;
const Category = tw.div`uppercase text-pbsGreen-500 text-xs font-bold tracking-widest leading-loose after:content after:block after:border-b-2 after:border-pbsGreen-500 after:w-8`;
const CreationDate = tw.div`mt-4 uppercase text-gray-600 italic font-semibold text-xs`;
const Title = tw.div`mt-1 font-black text-2xl text-gray-900 group-hover:text-pbsGreen-500 transition duration-300`;
const Description = tw.div``;

const ButtonContainer = tw.div`flex justify-center`;
const LoadMoreButton = tw(PrimaryButton)`mt-16 mx-auto`;
const CreateButton = tw(PrimaryButton)`mt-16 mx-auto`;

const ActionsRow = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
  display: flex;
  gap: 0.5rem;
`;
const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  background-color: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease;

  svg {
    width: 1rem;
    height: 1rem;
    color: #374151;
  }

  &:hover {
    background-color: #e5e7eb;
  }

  &:hover svg {
    ${tw`text-pbsGreen-500`}
  }
`;
const DeleteActionButton = styled(ActionButton)`
  &:hover svg {
    color: #dc2626;
  }
`;

export default function BlogIndex ({
  headingText = "Info Corner",
}) {
  const [posts, setPosts] = useState([]);
  const [visible, setVisible] = useState(7);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const isLoggedIn = Boolean(Cookies.get('id'));

  const fetchBlogs = () => {
    getBlogs().then((response) => {
      const blogs = response;
      console.log("Fetched blogs:", blogs);

      blogs.forEach((blog, index) => {
        blog.featured = index === 0; // Mark the first blog as featured
      });
      setPosts(blogs);
    }).catch(error => {
      console.error("Error fetching blogs:", error);
      setPosts([getPlaceholderPost()]); // Fallback to placeholder post on error
    });
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const onLoadMoreClick = () => {
    setVisible(v => v + 6);
  };

  const handleCreateClick = () => {
    setEditingPost(null);
    setShowModal(true);
  };

  const handleEditClick = (e, post) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingPost(post);
    setShowModal(true);
  };

  const handleDeleteClick = (e, post) => {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = window.confirm(`Delete "${post.title}"? This can't be undone.`);
    if (!confirmed) return;

    deleteBlog(post.id)
      .then(() => {
        fetchBlogs();
      })
      .catch(error => {
        console.error("Error deleting blog:", error);
        window.alert("Failed to delete the post. Please try again.");
      });
  };

  const handleModalVisibility = (nextVisible) => {
    setShowModal(nextVisible);
    if (!nextVisible) setEditingPost(null);
  };

  return (
    <>
    <Helmet>
      <title>Blogs | Placement & Business Services Ltd</title>
      <meta
        name="description"
        content="Check out our informative posts on employment topics."
      />

      <link rel="canonical" href="https://www.pbslja.com/about" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogsPage",
          "name": "Info Corner",
          "url": "https://www.pbslja.com/info-corner",
          "description": "Check out our informative posts on employment topics.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.pbslja.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blogs",
                "item": "https://www.pbslja.com/blogs"
              }
            ]
          }
        })}
      </script>
    </Helmet>
    <AnimationRevealPage>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>{headingText}</Heading>
          </HeadingRow>
          { isLoggedIn && (
            <CreateButton onClick={handleCreateClick}>Create Blog Post</CreateButton>
          )}
          <PostFormModal
            visible={showModal}
            setVisible={handleModalVisibility}
            post={editingPost}
            onSaved={fetchBlogs}
          />
          <Posts>
            {posts && posts.length > 0 && posts.slice(0, visible).map((post, index) => (
              <PostContainer key={index} featured={post.featured}>
                <Post className="group" as="a" href={"/info-corner/post?id=" + post.id}>
                  {isLoggedIn && (
                    <ActionsRow>
                      <ActionButton
                        type="button"
                        aria-label="Edit post"
                        onClick={(e) => handleEditClick(e, post)}
                      >
                        <EditIcon />
                      </ActionButton>
                      <DeleteActionButton
                        type="button"
                        aria-label="Delete post"
                        onClick={(e) => handleDeleteClick(e, post)}
                      >
                        <TrashIcon />
                      </DeleteActionButton>
                    </ActionsRow>
                  )}
                  <Image imageSrc={post.image_url} />
                  <Info>
                    <Category>{post.category}</Category>
                    <CreationDate>{post.created_date}</CreationDate>
                    <Title>{post.title}</Title>
                    {post.featured && post.description && <Description>{post.description}</Description>}
                  </Info>
                </Post>
              </PostContainer>
            ))}
          </Posts>
          {visible < posts.length && (
            <ButtonContainer>
              <LoadMoreButton onClick={onLoadMoreClick}>Load More</LoadMoreButton>
            </ButtonContainer>
          )}
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
    </>
  );
};

const getPlaceholderPost = () => ({
  imageSrc:
    "https://images.unsplash.com/photo-1418854982207-12f710b74003?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1024&q=80",
  category: "Travel Guide",
  date: "April 19, 2020",
  title: "Visit the beautiful Alps in Switzerland",
  description:
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.  Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
  url: "https://reddit.com"
});